import { mmFetchWithResponse } from 'api/mmFetchWithResponse';

/**
 * Merch line-item search: `GET /api/inventory/v1/fc-transfers/items`
 * (distinct from transfer-level `GET /api/inventory/v1/fc-transfers`).
 */
const FC_TRANSFER_ITEMS_SEARCH_PATH = 'api/inventory/v1/fc-transfers/items';

/** Filters sent as repeated `key[]=` positive ints (TEXT chips + multi SELECT ids). */
const MULTI_INT_FILTER_KEYS = [
    'transferId',
    'externalSku',
    'sourceLotId',
    'sourceFacilityId',
    'destinationFacilityId',
    'companySourceId',
];

/** Inclusive date-range bounds (`Y-m-d`). Start and/or end may be set. */
const DATE_RANGE_BOUND_KEYS = [
    'estimatedDepartureDateStart',
    'estimatedDepartureDateEnd',
    'receiptStartedAtStart',
    'receiptStartedAtEnd',
    'completedAtStart',
    'completedAtEnd',
    'createdAtStart',
    'createdAtEnd',
];

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * @param {unknown} value
 * @returns {number | undefined}
 */
function parseIntFilter(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    if (typeof value === 'number') {
        return Number.isInteger(value) && value > 0 ? value : undefined;
    }
    const trimmed = String(value).trim();
    if (!trimmed) return undefined;
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n <= 0 || String(n) !== trimmed) {
        return undefined;
    }
    return n;
}

/**
 * @param {unknown} value
 * @returns {number[]}
 */
function parseIntFilterList(value) {
    let raw;
    if (Array.isArray(value)) {
        raw = value;
    } else if (value === undefined || value === null || value === '') {
        raw = [];
    } else {
        raw = [value];
    }

    const parsed = [];
    for (const item of raw) {
        const n = parseIntFilter(item);
        if (n !== undefined) {
            parsed.push(n);
        }
    }
    return parsed;
}

/**
 * @param {URLSearchParams} queryParams
 * @param {string} key
 * @param {unknown} value
 */
function appendIntArrayParam(queryParams, key, value) {
    for (const n of parseIntFilterList(value)) {
        queryParams.append(`${key}[]`, String(n));
    }
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function parseStringFilterList(value) {
    let raw;
    if (Array.isArray(value)) {
        raw = value;
    } else if (value === undefined || value === null || value === '') {
        raw = [];
    } else {
        raw = [value];
    }

    const parsed = [];
    for (const item of raw) {
        if (item !== undefined && item !== null) {
            const trimmed = String(item).trim();
            if (trimmed) {
                parsed.push(trimmed);
            }
        }
    }
    return parsed;
}

/**
 * @param {URLSearchParams} queryParams
 * @param {string} key
 * @param {unknown} value
 */
function appendStringArrayParam(queryParams, key, value) {
    for (const s of parseStringFilterList(value)) {
        queryParams.append(`${key}[]`, s);
    }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function firstNonEmptyString(value) {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            if (item !== undefined && item !== null && String(item).trim()) {
                return String(item).trim();
            }
        }
    }
    return '';
}

/**
 * @param {unknown} value
 * @returns {string | undefined} Valid `Y-m-d` string, or undefined when missing/invalid
 */
function parseIsoDateFilter(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const trimmed = String(value).trim();
    if (!ISO_DATE_ONLY.test(trimmed)) {
        return undefined;
    }
    const [y, m, d] = trimmed.split('-').map((part) => Number(part));
    const asDate = new Date(y, m - 1, d);
    if (asDate.getFullYear() !== y || asDate.getMonth() !== m - 1 || asDate.getDate() !== d) {
        return undefined;
    }
    return trimmed;
}

/**
 * Builds URLSearchParams for `GET /inventory/v1/fc-transfers/items`.
 * Sort is fixed server-side (transferItemId desc) — no sort params.
 *
 * @param {Object} [params={}]
 * @returns {URLSearchParams}
 */
export function buildFCTransferItemsSearchParams(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.pageSize !== undefined && params.pageSize !== null && params.pageSize !== '') {
        queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.cursor !== undefined && params.cursor !== null && params.cursor !== '') {
        queryParams.set('cursor', String(params.cursor));
    }

    for (const key of MULTI_INT_FILTER_KEYS) {
        appendIntArrayParam(queryParams, key, params[key]);
    }

    appendStringArrayParam(queryParams, 'transferStatus', params.transferStatus ?? params.transferStatuses);

    const transferItemStatus = firstNonEmptyString(params.transferItemStatus);
    if (transferItemStatus) {
        queryParams.set('transferItemStatus', transferItemStatus);
    }

    if (params.hasDiscrepancy === true || params.hasDiscrepancy === 'true' || params.hasDiscrepancy === '1') {
        queryParams.set('hasDiscrepancy', 'true');
    } else if (params.hasDiscrepancy === false || params.hasDiscrepancy === 'false' || params.hasDiscrepancy === '0') {
        queryParams.set('hasDiscrepancy', 'false');
    }

    for (const key of DATE_RANGE_BOUND_KEYS) {
        const date = parseIsoDateFilter(params[key]);
        if (date) {
            queryParams.set(key, date);
        }
    }

    return queryParams;
}

/**
 * @param {Object} [params={}]
 * @returns {string}
 */
export function buildFCTransferItemsSearchQueryString(params = {}) {
    return buildFCTransferItemsSearchParams(params).toString();
}

/**
 * Search FC transfer line items (`GET /inventory/v1/fc-transfers/items`).
 *
 * @param {Object} [params={}]
 * @param {Object} [options={}]
 * @returns {Promise<{status: boolean, msg?: string, data?: {rows: Array, pagination: Object}}>}
 */
export async function searchFCTransferItems(params = {}, options = {}) {
    const qs = buildFCTransferItemsSearchQueryString(params);
    const url = qs.length > 0 ? `${FC_TRANSFER_ITEMS_SEARCH_PATH}?${qs}` : FC_TRANSFER_ITEMS_SEARCH_PATH;
    return mmFetchWithResponse(url, options);
}
